import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { TemuShopProvider, useTemuShop } from '@/temu-platform/context/TemuShopContext';
import { TemuSyncJobProvider } from '@/temu-platform/context/TemuSyncJobContext';
import TemuShopTopBar from '@/temu-platform/layout/TemuShopTopBar';
import TemuGlobalSyncProgress from '@/temu-platform/components/TemuGlobalSyncProgress';
import TemuModuleTrackGuard from '@/temu-platform/components/TemuModuleTrackGuard';
import TemuNoticeDrawerHost from '@/temu-platform/components/TemuNoticeDrawerHost';
import PlatformShopPickModal from '@/components/platform/PlatformShopPickModal';
import {
  IPE_TEMU_BASE_PATH,
  TEMU_SHOP_SWITCH_TOAST_KEY,
  buildTemuChildPath,
  ensureTemuWorkspaceTokenShared,
  navItemsForShopTypeSlug,
  openTemuPathInNewTab,
  parseTemuShopTypeSlugFromPath,
} from '@/temu-platform/constants/temuRoutes';
import { isAllShopsScope, isConcreteShopId } from '@/utils/platformShopScope';
import { showToast } from '@/utils/toast';
import { platformNavLabel } from '@/i18n/platformNavLabel';
import '@/styles/temu-platform/temu-platform.css';

const NAV_EXPAND_STORAGE_KEY = 'ipe_temu_nav_expanded';

function appendShopIdQuery(path, shopId) {
  const base = String(path || '');
  if (!isConcreteShopId(shopId)) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}shopId=${encodeURIComponent(String(shopId))}`;
}

function isNavGroupActive(pathname, parentPath) {
  const slug = parseTemuShopTypeSlugFromPath(pathname);
  const base = slug
    ? `${IPE_TEMU_BASE_PATH}/${slug}/${parentPath}`
    : `${IPE_TEMU_BASE_PATH}/${parentPath}`;
  return pathname === base || pathname.startsWith(`${base}/`);
}

function collectActiveGroupIds(pathname) {
  const slug = parseTemuShopTypeSlugFromPath(pathname) || 'semi';
  return navItemsForShopTypeSlug(slug)
    .filter((item) => Array.isArray(item.children) && item.children.length > 0)
    .filter((item) => isNavGroupActive(pathname, item.path))
    .map((item) => item.id);
}

function readStoredExpanded() {
  try {
    const raw = sessionStorage.getItem(NAV_EXPAND_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : null;
  } catch {
    return null;
  }
}

function writeStoredExpanded(ids) {
  try {
    sessionStorage.setItem(NAV_EXPAND_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

function TemuSidebarNav({ expandedIds, toggleGroup, pathname }) {
  const { t } = useTranslation('workbench');
  const navText = (item) => platformNavLabel(t, item);
  const slug = parseTemuShopTypeSlugFromPath(pathname) || 'semi';
  const navItems = useMemo(() => navItemsForShopTypeSlug(slug), [slug]);
  const { activeShopId, scopedShops = [], isAllShopsMode } = useTemuShop();
  const [pickOpen, setPickOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState('');

  const interceptComingSoon = (e, label) => {
    e.preventDefault();
    e.stopPropagation();
    showToast(t('platformNav.comingSoon', { label }), 'info');
  };

  const openNewTabPath = (to) => {
    if (isAllShopsMode || isAllShopsScope(activeShopId)) {
      setPendingPath(to);
      setPickOpen(true);
      return;
    }
    const concrete = isConcreteShopId(activeShopId) ? activeShopId : '';
    openTemuPathInNewTab(appendShopIdQuery(to, concrete));
  };

  return (
    <>
    <nav className="temu-platform__sidebar" aria-label={t('platformNav.menuTemu')}>
      {navItems.map((item) => {
        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        if (!hasChildren) {
          if (item.comingSoon) {
            return (
              <a
                key={item.id}
                href={buildTemuChildPath(item.path, '')}
                className="temu-platform__nav-item temu-platform__nav-item--soon"
                aria-disabled="true"
                onClick={(e) => interceptComingSoon(e, navText(item))}
              >
                {navText(item)}
              </a>
            );
          }
          return (
            <NavLink
              key={item.id}
              to={item.path}
              className={({ isActive }) =>
                `temu-platform__nav-item${isActive ? ' temu-platform__nav-item--active' : ''}`
              }
            >
              {navText(item)}
            </NavLink>
          );
        }

        const routeActive = isNavGroupActive(pathname, item.path);
        const expanded = expandedIds.includes(item.id);
        const panelId = `temu-nav-panel-${item.id}`;

        return (
          <div
            key={item.id}
            className={`temu-platform__nav-group${expanded ? ' temu-platform__nav-group--open' : ''}${routeActive ? ' temu-platform__nav-group--active' : ''}`}
          >
            <button
              type="button"
              className={`temu-platform__nav-group-toggle${routeActive ? ' is-active' : ''}`}
              aria-expanded={expanded}
              aria-controls={panelId}
              onClick={() => toggleGroup(item.id)}
            >
              <span className="temu-platform__nav-group-label">{navText(item)}</span>
              <ChevronDown
                size={14}
                strokeWidth={2.25}
                className={`temu-platform__nav-group-chevron${expanded ? ' is-open' : ''}`}
                aria-hidden="true"
              />
            </button>
            <div
              id={panelId}
              className="temu-platform__nav-children"
              role="group"
              aria-label={t('platformNav.submenu', { label: navText(item) })}
              hidden={!expanded}
            >
              {item.children.map((child) => {
                const to = buildTemuChildPath(item.path, child.path);
                const className = 'temu-platform__nav-item temu-platform__nav-item--child';
                const soon = Boolean(item.comingSoon || child.comingSoon);
                if (soon) {
                  return (
                    <a
                      key={child.id}
                      href={to}
                      className={`${className} temu-platform__nav-item--soon`}
                      aria-disabled="true"
                      onClick={(e) => interceptComingSoon(e, navText(child) || navText(item))}
                    >
                      {navText(child)}
                    </a>
                  );
                }
                if (child.openInNewTab) {
                  return (
                    <a
                      key={child.id}
                      href={to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={className}
                      onClick={(e) => {
                        e.preventDefault();
                        openNewTabPath(to);
                      }}
                    >
                      {navText(child)}
                    </a>
                  );
                }
                return (
                  <NavLink
                    key={child.id}
                    to={to}
                    end={child.path === '' || child.id === 'info' || child.id === 'reviews'}
                    className={({ isActive }) =>
                      `${className}${isActive ? ' temu-platform__nav-item--active' : ''}`
                    }
                  >
                    {navText(child)}
                  </NavLink>
                );
              })}
            </div>
          </div>
        );
      })}
    </nav>
    <PlatformShopPickModal
      open={pickOpen}
      shops={scopedShops}
      title={t('platformBar.pickPublishShop')}
      description={t('platformBar.pickPublishShopDesc')}
      confirmText={t('platformBar.openPublishPage')}
      onClose={() => {
        setPickOpen(false);
        setPendingPath('');
      }}
      onConfirm={(shopId) => {
        setPickOpen(false);
        const path = pendingPath;
        setPendingPath('');
        if (path) openTemuPathInNewTab(appendShopIdQuery(path, shopId));
      }}
    />
    </>
  );
}

export default function TemuPlatformLayout() {
  const location = useLocation();
  const hideWorkspaceChrome = isNavGroupActive(location.pathname, 'products')
    || isNavGroupActive(location.pathname, 'compliance')
    || isNavGroupActive(location.pathname, 'templates')
    || isNavGroupActive(location.pathname, 'orders')
    || isNavGroupActive(location.pathname, 'after-sales')
    || isNavGroupActive(location.pathname, 'finance')
    || isNavGroupActive(location.pathname, 'shop')
    || isNavGroupActive(location.pathname, 'home')
    || isNavGroupActive(location.pathname, 'discuss')
    || isNavGroupActive(location.pathname, 'stock')
    || isNavGroupActive(location.pathname, 'marketing')
    || isNavGroupActive(location.pathname, 'fulfillment')
    || location.pathname.startsWith(`${IPE_TEMU_BASE_PATH}/certificates`);

  const activeGroupIds = useMemo(
    () => collectActiveGroupIds(location.pathname),
    [location.pathname],
  );

  const [expandedIds, setExpandedIds] = useState(() => {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const stored = readStoredExpanded();
    const active = collectActiveGroupIds(pathname);
    if (stored) {
      return Array.from(new Set([...stored, ...active]));
    }
    const slug = parseTemuShopTypeSlugFromPath(pathname) || 'semi';
    return active.length > 0
      ? active
      : navItemsForShopTypeSlug(slug).filter((item) => item.children?.length).map((item) => item.id);
  });

  useEffect(() => {
    ensureTemuWorkspaceTokenShared();
  }, []);

  useEffect(() => {
    if (activeGroupIds.length === 0) return;
    setExpandedIds((prev) => {
      const next = Array.from(new Set([...prev, ...activeGroupIds]));
      if (next.length === prev.length && next.every((id) => prev.includes(id))) {
        return prev;
      }
      writeStoredExpanded(next);
      return next;
    });
  }, [activeGroupIds]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(TEMU_SHOP_SWITCH_TOAST_KEY);
      if (!raw) return;
      sessionStorage.removeItem(TEMU_SHOP_SWITCH_TOAST_KEY);
      const payload = JSON.parse(raw);
      if (payload?.message) {
        showToast(payload.message, payload.type || 'success');
      }
    } catch {
      sessionStorage.removeItem(TEMU_SHOP_SWITCH_TOAST_KEY);
    }
  }, []);

  const toggleGroup = (id) => {
    setExpandedIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id];
      writeStoredExpanded(next);
      return next;
    });
  };

  return (
    <TemuShopProvider>
      <TemuSyncJobProvider>
        <div className={`temu-platform${hideWorkspaceChrome ? ' temu-platform--product-ops' : ''}`} data-testid="temu-platform-layout">
          <TemuShopTopBar />
          <TemuNoticeDrawerHost />
          <div className="temu-platform__body">
            <TemuSidebarNav
              expandedIds={expandedIds}
              toggleGroup={toggleGroup}
              pathname={location.pathname}
            />
            <main className={`temu-platform__main${hideWorkspaceChrome ? ' temu-platform__main--product-ops' : ''}`}>
              <TemuGlobalSyncProgress />
              <TemuModuleTrackGuard>
                <Outlet />
              </TemuModuleTrackGuard>
            </main>
          </div>
        </div>
      </TemuSyncJobProvider>
    </TemuShopProvider>
  );
}
